import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import CreateTimeContext from './index';
import { addDays, addHours, endOfDay, startOfDay } from 'date-fns';
import moment from 'moment';
import { useBooksData } from '../../selectors/books';

function setTimeZone(date) {
  date.setHours(date.getHours() + new Date().getTimezoneOffset() / 60);
  return date;
}

export const CurrentTimeProvider = ({ children }) => {
  const book_data = useBooksData();
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [currentBook, setCurrentBook] = React.useState({ index: -1, book: {} });
  const [currentPage, setCurrentPage] = React.useState(0);
  const [fetchRanges, setFetchRanges] = React.useState({
    startRange: startOfDay(currentDate),
    endRange: endOfDay(currentDate),
  });

  useEffect(() => {
    setFetchRanges({
      startRange: addDays(startOfDay(currentDate), currentPage),
      endRange: addDays(endOfDay(currentDate), currentPage),
    });
  }, [currentPage]);

  useLayoutEffect(() => {
    let timer;
    timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const books = useMemo(() => {
    let result = [];
    let sorted = book_data
      .map(({ startTime, endTime, checkInTime, ...rest }) => ({
        startTime: checkInTime ? new Date(checkInTime) : new Date(startTime),
        endTime: new Date(endTime),
        ...rest,
      }))
      .sort((a, b) => a.startTime - b.startTime);

    let startWork =
      addHours(fetchRanges.startRange, 9) < currentDate ? currentDate : addHours(fetchRanges.startRange, 9);
    let endWork = addHours(fetchRanges.startRange, 9 + 10);

    sorted.forEach((item, index, arr) => {
      let { startTime, endTime } = item;
      const { startTime: nextStartTime } = arr[index + 1] || {};
      const { startTime: prevStartTime } = arr[index + -1] || {};
      const { endTime: nextEndTime } = arr[index + 1] || {};
      // first space work start
      if (
        index === 0 &&
        startTime > startWork &&
        moment.duration(moment(startTime).diff(moment(startWork))).asMinutes() >= 15
      ) {
        result.push({
          startTime: startWork,
          endTime: startTime,
          isEmpty: true,
        });
      }

      result.push(item);

      if (index === arr.length - 1 && moment.duration(moment(endWork).diff(moment(endTime))).asMinutes() >= 15) {
        result.push({
          startTime: endTime < startWork ? startWork : endTime,
          endTime: endWork,
          isEmpty: true,
        });
      } else if (nextEndTime > startWork && moment.duration(moment(endWork).diff(moment(endTime))).asMinutes() >= 15) {
        result.push({
          startTime: endTime < startWork ? startWork : endTime,
          endTime: nextStartTime < endWork ? nextStartTime : endWork,
          isEmpty: true,
        });
      }
    });

    if (currentDate < endWork && !result.length)
      result.push({
        isEmpty: true,
        startTime: startWork,
        endTime: endWork,
      });

    result = result.filter(
      (item) => moment.duration(moment(item.startTime).diff(moment(item.endTime))).asMinutes() !== 0,
    );
    return !currentPage
      ? result.filter((item) => !item.startTime <= currentDate && item.endTime >= currentDate)
      : result;
  }, [book_data]);

  useEffect(() => {
    const currDate = currentDate;
    const foundIndex = books.findIndex((item) => item.startTime <= currDate && item.endTime >= currentDate);
    setCurrentBook({ index: foundIndex, book: books[foundIndex] });
  }, [books]);

  const nextBook = useMemo(() => books[currentBook.index + 1] || {}, [books, currentBook]);

  const bookForCheckIn = useMemo(() => {
    if (currentBook.book?._id && !currentBook.book?.isEmpty && !currentBook.book?.checkIn) return currentBook.book;
    else if (
      nextBook.startTime &&
      moment.duration(moment(nextBook.startTime).diff(moment(currentDate))).asMinutes() <= 15 &&
      !nextBook.isEmpty &&
      !nextBook.checkIn
    )
      return nextBook;

    return false;
  }, [currentBook, nextBook]);

  return (
    <CreateTimeContext.Provider
      value={{
        currentDate,
        bookings: books,
        currentBook,
        bookForCheckIn,
        nextBook,
        fetchRanges,
        currentPage,
        setCurrentPage,
      }}
    >
      {children}
    </CreateTimeContext.Provider>
  );
};
